import { useState, useEffect, useRef, useMemo } from 'react';
import { bool, string } from 'prop-types';
import { Modal, Spin } from 'antd';

import RecommendedBag from 'components/recommendedBag';
import { formatCurrency, getCookie, COOKIES, eraseCookie, getFixedPrice, obtainVoucherPrices } from 'utils/helpers';

import { IS_CAMPER } from 'utils/constants/system';
import { defaultLocale } from 'utils/translations';
import { getCart } from 'actions/products';
import { CloseModalSVG, CloseSVG } from 'components/iconsSVG/close';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { parseBagEcommerceGTM, parseBagItemsEcommerceGA4 } from 'utils/gtmUtils';

import { useUser } from 'context/user';
import { useI18n } from 'context/i18n';
import useDelayedState from 'hooks/useDelayedState';
import { useIsMounted, useLink, useLocale } from 'hooks';
import { canUserHover } from 'utils';
import { MiniCartItem } from './mini-bag-item';

import { LabCartIcon } from 'components/camperlab/icons/cart';
import { getDomainValue } from 'utils/domain';
import { sortAndOrderMiniBag } from 'utils/bag';

import styles from './style.module.css';

export function MiniBag({ isInSalePath = false }) {
  const {
    t,
    profileData,
    setBagLength,
    bag,
    setBag,
    setBagHasMemberOnlyProduct,
    setRecommendedProductsForBag,
    promoLabel,
    setPromoLabel,
    promoLabelType,
    setPromoLabelType,
  } = useI18n();
  const { contextFilters, activatedPrevouchers } = useUser;
  const isMounted = useIsMounted();
  const { locale, country: market } = useLocale();
  const to = useLink();
  // const [ bag, setBag ] = useState(null);
  const [visible, setVisible] = useDelayedState(false);
  const [loading, setLoading] = useState(false);
  const { emptyBagCollections, config } = profileData;
  const bagRef = useRef(null);
  const timer = useRef();
  const previousBagLength = useRef(undefined);
  const initialLoad = useRef(true);

  const showFreeShipping = config?.show_free_shipping || false;
  const showGiftPurchase = config?.show_gift_purchase || false;
  const showProducts = bag && bag.length > 0;
  const orderedBag = useMemo(() => sortAndOrderMiniBag({ bag: bag ?? [], locale, currency: profileData?.currencySap ?? 'EUR' }), [bag, locale]);
  let total =
    orderedBag && orderedBag.length > 0 ?
      JSON.parse(JSON.stringify(orderedBag)).reduce((accumulator, current) => {
        if (current.parentId) return accumulator;
        const voucherPriceInfo = obtainVoucherPrices({ ...current, prices: current.price }, t, locale, contextFilters, activatedPrevouchers, false, true);
        let currentPrice = current.price.current;

        if (voucherPriceInfo) {
          if (!voucherPriceInfo?.message) {
            currentPrice = Math.round(voucherPriceInfo.info.current);
          }
        }
        return accumulator + currentPrice;
      }, 0)
    : 0;

  if (total && typeof total === 'number') {
    total = total.toFixed(2);
  }

  const currency = orderedBag && orderedBag.length > 0 ? formatCurrency(bag[0].price.currency) : '';
  const onlyOneItem = orderedBag && orderedBag.length === 1;
  const sunneiProducts = orderedBag?.filter((item) => item.sunnei === true);
  const hasOnlyOneSunnei = sunneiProducts?.length === 1;

  const handleOnClickRemoveBtn = async (id) => {
    setLoading(true);
    const cookiesBagId = getCookie(COOKIES.BAG);
    const employeeId = getCookie(COOKIES.EMPLOYEE_ID) || '';
    const voucher = getCookie(COOKIES.BONDS) || '';
    const zipCode = getCookie(COOKIES.ZIP) || '';
    // const newBag = JSON.parse(JSON.stringify(bag));
    const itemIndex = bag.findIndex((item) => item.id === id);
    // const itemToDelete = newBag[itemIndex];
    // newBag.splice(itemIndex, 1);
    // const newBagFormatted = JSON.parse(JSON.stringify(newBag)).map((product) => ({
    //   code: product.productId,
    //   ...product,
    // }));
    const tax = zipCode && zipCode !== '' ? { zipcode: zipCode } : {};

    // const { productId } = itemToDelete;
    let itemToDelete = {};
    // Filtramos la bolsa para quitar el producto y cualquier producto que pertenezca a ese pack
    const filteredNewBag = bag.reduce((acc, bagItem) => {
      if (bagItem.id === id || bagItem.parentId === id) {
        if (bagItem.id === id) itemToDelete = bagItem;
        return acc;
      }
      return [...acc, { code: bagItem.productId, ...bagItem }];
    }, []);
    // Aqui vemos si queda en la bolsa algun producto con el mismo productId que el que vamos a borrar y si no lo hay quitamos de los recomendados aquellos con su originalProductId igual al productId del producto que vamos a borrar
    const hasSomeOfTheSameID = filteredNewBag.some((item) => item.productId === itemToDelete.productId);
    if (!hasSomeOfTheSameID) {
      setRecommendedProductsForBag((prev) =>
        prev.filter((recommendedItem) => recommendedItem.originalProductId !== productId).map((item, i) => ({ ...item, order: i })),
      );
    }

    try {
      let updatedPromoLabel = null;
      const result = await getCart({
        lang: locale,
        products: filteredNewBag,
        bondValue: voucher,
        tax,
        hashOrderId: cookiesBagId,
        employeeId,
      });
      if (result) {
        dataLayerHandleEvent({
          event: 'remove_from_cart',
          ecommerce: {
            currency: profileData?.currencySap,
            value: itemToDelete?.price?.current,
            items: parseBagItemsEcommerceGA4([itemToDelete], itemIndex + 1),
          },
          cart: {
            items: parseBagItemsEcommerceGA4(filteredNewBag),
          },
        });
        let updatedPromoLabelType = 'progress';
        if (showFreeShipping) {
          updatedPromoLabel = result?.freeShippingLabel || null;
        }
        if (showGiftPurchase) {
          updatedPromoLabel = result?.gwplabel || result?.gwplabelObtained || result?.freeShippingLabel || null;
          if (result?.gwplabelObtained && result?.gwplabelObtained !== '') {
            updatedPromoLabel = result?.gwplabelObtained;
            updatedPromoLabelType = 'obtained';
          }
        }
        const newShipments = result.productShipmentCost.shipmentsCosts;
        const isEmptyBag = newShipments.length === 0 || result.productShipmentCost.products.length === 0;
        if (filteredNewBag.length === 0 || isEmptyBag) {
          eraseCookie(COOKIES.BAG);
          eraseCookie(COOKIES.BAG_DATE);
        }
        setBagLength(isEmptyBag ? 0 : filteredNewBag.length);
        const hasMemberOnlyProduct = isEmptyBag ? false : filteredNewBag.filter((product) => product.membersOnly === true).length > 0;
        setBagHasMemberOnlyProduct(hasMemberOnlyProduct);
        setBag(isEmptyBag ? [] : filteredNewBag);
        setPromoLabel(updatedPromoLabel);
        setPromoLabelType(updatedPromoLabelType);
        setLoading(false);
      }
    } catch (error) {
      // handle error
      setLoading(false);
      console.error(error);
      Modal.error({
        title: t('bolsa', 'error.borrar.producto.titulo', 'An error has occurred'),
        content: t('bolsa', 'error.borrar.producto.descripcion', 'We are sorry. The product could not be removed.'),
      });
    }
  };

  useEffect(() => {
    if (window !== undefined) {
      window.showMiniBag = () => {
        setVisible(true);
      };
    }
  }, []);

  useEffect(() => {
    const showBagIfItemAdded = () => {
      if (bagRef.current?.offsetParent === null) return;
      if (initialLoad.current) return;
      if (previousBagLength.current === undefined) {
        return;
      }
      if (previousBagLength.current >= bag.length) {
        return;
      }
      setVisible(true);
      timer.current = setTimeout(() => {
        if (isMounted()) {
          setVisible(false);
        }
      }, 6000);
    };

    showBagIfItemAdded();
    if (bag) {
      initialLoad.current = false;
      previousBagLength.current = bag.length;
    }

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [bag]);

  const handleClickMiniBagIcon = () => {
    const canHover = canUserHover();
    if (!canHover) {
      setVisible((prev) => !prev);
      return;
    }

    if (canHover && bag && bag.length > 0) {
      window.location.assign(to('/bolsa'));
    }
  };

  const handleMouseEnter = () => {
    const canHover = canUserHover();
    if (!canHover) return;
    clearTimeout(timer.current);
    setVisible(true);
  };

  const handleMouseLeave = () => {
    const canHover = canUserHover();
    if (!canHover) return;
    setVisible(false, 300);
  };

  // Si un usuario interactua con la minibolsa en movil quitar el timeout para que no se cierre.
  const handleInteraction = () => {
    clearTimeout(timer.current);
  };

  const defaultBagIcon = (
    <button onClick={handleClickMiniBagIcon} className={bag && bag.length > 0 ? styles.bagLink : `${styles.bagLink} ${styles.emptyBag}`}>
      {bag && <span className={styles.bagItemsCount}>{!isInSalePath && bag.length > 0 ? bag.length : ''}</span>}
    </button>
  );

  const camperlabIcon = (
    <button className="flex items-center font-primary uppercase" onClick={handleClickMiniBagIcon}>
      <>
        <LabCartIcon id="lab-cart-icon" className="flex md:hidden" height={20} width={20} />
        <span id="lab-cart-text" className="hidden text-[0.75rem] md:flex">
          {t('mi.bolsa', 'bag.widget')}
        </span>
      </>
      {bag && !isInSalePath && bag?.length > 0 ?
        <span id="lab-cart-count" className="mt-[2px] hidden min-w-[3em] md:flex">
          ( {bag.length} )
        </span>
      : ''}
    </button>
  );

  const miniBagIcon = getDomainValue({
    camper: defaultBagIcon,
    nnormal: defaultBagIcon,
    camperlab: camperlabIcon,
  });

  return (
    <nav ref={bagRef} className={styles.bagIcon} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {miniBagIcon}
      {visible && !isInSalePath ?
        <div className={bag && bag.length > 0 ? styles.bagMenu : `${styles.bagMenu} ${styles.emptyMenu}`} onPointerDown={handleInteraction}>
          {showProducts && (
            <div className={styles.bagOverlay}>
              <div className="mr-4 flex items-center justify-between gap-4 border-b border-[var(--color-divider)] pb-[5px]">
                <p className={`${styles.menuItemTitle} mb-0 text-xs uppercase nnormal:font-secondary`}>{t('menu.superior', 'mi.bolsa')}</p>
                <button className="flex items-center justify-center" onClick={() => setVisible(false)}>
                  <CloseModalSVG className="size-4" />
                </button>
              </div>
              <Spin spinning={loading}>
                <ul className={styles.bagList}>
                  <div className={`${styles.itemsWrapper} ${onlyOneItem ? styles.noScroll : ''}`}>
                    {orderedBag.map((product, index) => {
                      return (
                        <li
                          data-ge-basket-cartitemid={product?.globaleId || product?.productId}
                          key={`${product.productId}-${index}`}
                          className={styles.productItem}
                        >
                          <MiniCartItem product={product} onDelete={handleOnClickRemoveBtn} />
                          {Array.isArray(product.additionalProducts) && product.additionalProducts.length > 0 ?
                            <ul className={styles.additionalProducts}>
                              {product.additionalProducts.map((additionalProduct, additionalIndex) => (
                                <li key={`${additionalProduct.productId}-${additionalIndex}`} className={styles.productItem}>
                                  <MiniCartItem product={additionalProduct} />
                                </li>
                              ))}
                            </ul>
                          : null}
                        </li>
                      );
                    })}
                    {/* {bag.map((product, index) => {
                      let parsedTarget = 'M';
                      if (market === 'US') {
                        parsedTarget = 'US';
                      } else if (['M', 'W'].includes(product.target)) {
                        parsedTarget = product.target;
                      }
                      let sizeLabel = null;
                      if (IS_CAMPER) {
                        const useSizeEquivalence =
                          product.size &&
                          product.sizeLabel &&
                          product.size !== product.sizeLabel &&
                          [FAMILIES.SHOES, FAMILIES.ACCESSORIES].includes(Number(product.family));
                        sizeLabel =
                          useSizeEquivalence && !TARGET_SIZE_LABEL_MARKETS.includes(market) ?
                            <span className={styles.size}>{`${product.sizeLabel} US / ${product.size} EU`}</span>
                          : <span className={styles.size}>
                              {product.camperLabPan === true ?
                                'US'
                              : product.sizeLabel || product.size ?
                                country
                              : ''}{' '}
                              {TARGET_SIZE_LABEL_MARKETS.includes(market) && product.family && Number(product.family) === FAMILIES.SHOES ?
                                `${product.gender ?? product.target} ${product.sizeLabel || product.size}`
                              : product.sizeLabel || product.size}
                            </span>;
                      }

                      const fixedPrice = getFixedPrice(market, profileData, product?.price?.current);
                      const voucherPriceInfo = obtainVoucherPrices(
                        { ...product, prices: product.price },
                        t,
                        locale,
                        contextFilters,
                        activatedPrevouchers,
                        false,
                        true,
                      );
                      let info = {};
                      const oldDiscount = product.price.discount;

                      if (voucherPriceInfo) {
                        info = voucherPriceInfo.info || {};

                        if (!voucherPriceInfo?.message) {
                          product.price.voucherDiscount = info.discount;

                          const infoOverride = { ...info };

                          delete infoOverride.discount;
                          product.price = { ...product.price, ...infoOverride };
                        }
                      }

                      // original price
                      // [previous] -[siempre voucherDiscount] [current]
                      // [promocode applied]

                      // final price
                      // if discount
                      // [previous] -[discount] [current]
                      // [extra [-voucherDiscount] applied]
                      // original price

                      const { price } = product;
                      const { voucherType } = price;
                      let { voucherDiscount } = price;

                      if (voucherDiscount !== undefined && voucherDiscount < 0) {
                        voucherDiscount *= -1;
                      }

                      const discountToDisplay =
                        oldDiscount ?
                          voucherType === 'original.price' ?
                            voucherDiscount || oldDiscount
                          : oldDiscount
                        : voucherDiscount || oldDiscount;

                      const voucherTypeToUseInLabel = oldDiscount !== undefined ? voucherType : 'original.price';
                      let appliedLabel = t('listado.productos', `voucher.price.message.${voucherTypeToUseInLabel}.applied`);

                      if (voucherType === 'final.price' && oldDiscount !== undefined) {
                        appliedLabel = appliedLabel.replace('<TAG discount>', voucherDiscount);
                      }

                      const appliedLabelToDisplay = info?.appliedLabel || voucherType ? appliedLabel : null;

                      return (
                        <li data-ge-basket-cartitemid={product.productId} key={`${product.productId}-${index}`} className={styles.productItem}>
                          <div className={styles.menuItem}>
                            <a href={product.url}>
                              <ProgressiveImg
                                src={product.thumbnail}
                                alt={`bag image for ${product.productId} ${index}`}
                              />
                            </a>
                            <div className={styles.productInfo}>
                              <p className={styles.productInfoName}>{product.name}</p>
                              <p className={IS_NNORMAL ? styles.productDescription : undefined}>
                                {IS_NNORMAL ? product.subtitleH1 : product.productLabel || product.productId}
                              </p>
                              <p>
                                {IS_NNORMAL ?
                                  <span className={styles.productDescription}>{nnormalSizesMap?.[parsedTarget]?.[product.size] ?? product.size}</span>
                                : sizeLabel}
                              </p>
                              <p className={styles.productInfoPrice}>
                                {product.price.previous && product.price.previous > product.price.current && (
                                  <strike data-ge-basket-productlistprice="data-ge-basket-productlistprice" className={styles.itemDiscount}>
                                    {product.price.previousFormated || `${product.price.previous} ${formatCurrency(product.price.currency)}`}
                                  </strike>
                                )}
                                {discountToDisplay && discountToDisplay > 0 && (
                                  <span
                                    className={`${product.price.discountFromVoucher ? `${styles.itemDiscount} ${styles.discountByVoucher}` : styles.itemDiscount} green`}
                                  >{`-${discountToDisplay}%`}</span>
                                )}
                                <span data-fp={fixedPrice} data-ge-price="data-ge-price" data-ge-basket-productsaleprice="data-ge-basket-productsaleprice">
                                  {product.price.currentFormated || `${product.price.current} ${formatCurrency(product.price.currency)}`}
                                </span>
                              </p>
                              {appliedLabelToDisplay ?
                                <span className={`${styles.productInfoPrice} ${styles.discountByVoucher}`}>{appliedLabelToDisplay}</span>
                              : null}
                            </div>
                            <div className={styles.removeProduct} onClick={() => handleOnClickRemoveBtn(index)}>
                              <CloseSVG />
                            </div>
                          </div>
                        </li>
                      );
                    })} */}
                    {/* {IS_NNORMAL && total >= GIFT_MIN_TOTAL_PRICE && renderGifHat &&
                <li data-ge-basket-cartitemid={'N1ARC02-001'} key={'gift-free-cap'} className={styles.productItem}>
                  <div className={styles.menuItem}>
                    <picture>
                      <img
                        src={'/assets-new/nnormal/N1ARC02-001_L.jpg' }
                        alt={'Image of gift Bag photo'}
                        width={80}
                        height={80}
                      />
                    </picture>
                    <div className={styles.productInfo}>
                      <p className={styles.productInfoName}>{t('menu.derecha', 'free.cap.item.price', '***Limited-edition tote bag')}</p>
                      <p>
                        <span data-ge-basket-productsaleprice="data-ge-basket-productsaleprice" >{t('menu.derecha', 'free.cap.item.name', '***FREE')}</span>
                      </p>
                    </div>
                    <div className={styles.removeProduct}></div>
                  </div>
                </li>
              } */}
                  </div>
                  <li className={styles.btnPurchase} key="gotobag" id="bag-purchase-btn">
                    <div className={styles.purchaseWrapper}>
                      {hasOnlyOneSunnei && <p className="error" dangerouslySetInnerHTML={{ __html: t('bolsa', 'completa.sunnei') }} />}
                      {promoLabel && <p className={promoLabelType === 'obtained' ? styles.promoLabelObtained : styles.promoLabel}>{promoLabel}</p>}
                      <p className={styles.totalPrice}>
                        <span>{'Total'}</span>
                        <span data-fp={getFixedPrice(market, profileData, total)} data-ge-basket-totals="data-ge-basket-totals">{`${total} ${currency}`}</span>
                      </p>
                      <p className={styles.taxes}>
                        {IS_CAMPER && market !== 'US' && market !== 'CA' && <span> {t('mis.pedidos', 'impuestos.incluidos', 'All taxes included')}</span>}
                        {profileData.freeShipping && profileData.freeShipping === true && profileData.freeShippingOver && profileData.freeShippingOver < total ?
                          <span className={styles.taxesPrices}>{t('mis.pedidos', 'free.shipping.included', 'Free Shipping')}</span>
                        : null}
                      </p>
                      <a href={`/${locale}/bolsa`} className={styles.btnGoPurchase}>
                        <p>{`${t('menu.derecha', 'derecha.comprar')} (${bag.length})`}</p>
                      </a>
                    </div>
                  </li>
                  <RecommendedBag />
                </ul>
              </Spin>
            </div>
          )}
          {!showProducts && (
            <div className={styles.bagOverlay}>
              <p>{t('menu.derecha', 'derecha.bolsa.vacia')}</p>
              <p>{t('menu.derecha', 'take.look.collection', '***Take a look at our collection.')}</p>
              <div className={styles.buttonContainer}>
                {emptyBagCollections?.map((item) => (
                  <a key={`collectionButton-${item.title}`} href={item.url} className={styles.collectionButton}>
                    {item.title}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      : null}
    </nav>
  );
}

MiniBag.propTypes = {
  locale: string,
  isInSalePath: bool,
};

MiniBag.displayName = 'MiniBag';
